import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { withLocalize } from 'react-localize-redux';
import AcceptInvitation from '../../components/invitation/AcceptInvitation';
import CloudFlare from '../../components/general/CloudFlare/CloudFlare';
import {
    acceptTokenInvitation,
    fetchInvitationStatus,
    registerInvitedResearcher,
    requestInvitationResend,
} from '../../services/invitations';
import { signIn } from '../../services/authService';
import { isArgon2Auth } from '../../utils/crypto';
import { fetchInvestigations } from '../../services';
import {
    ACCEPT_INVITATION_PATH,
    consumeAcceptFragment,
    stashPendingInvitation,
    takeBootAcceptFragment,
    takePendingInvitation,
} from '../../utils/invitationLink';
import { SIGN_IN_ROUTE } from '../../routes/urls';

/**
 * SHER-803 - the page an invite link opens: /accept-invitation#<invitationUuid>.<S>.<wrapSalt>.
 *
 * The secret is read from the fragment once and wiped from the address bar before anything else
 * runs (see consumeAcceptFragment); from then on it lives only in `fragment.current`. The only
 * exception is the sign-in round trip of an existing account, for which it is parked in this tab's
 * sessionStorage and taken back on return.
 *
 * Journeys (develop-skincair): new account -> register (with CAPTCHA, the invitation proof and
 * the investigation's KEKs re-wrapped under the new account's key; registerV2 accepts on this line,
 * all in one transaction) -> sign in, done; existing account -> sign in if needed -> accept, which
 * redeems the token and stores the re-wrapped KEKs in one call. The KEKs come from
 * `/invitation/status` and open only with this link (services/invitations.ts). A sha256 deployment
 * registers and then accepts with the v1 key, as on develop.
 */

const VIEW_FOR_STATE = { invalid: 'invalid', expired: 'expired', used: 'used', error: 'error' };

function hasSession() {
    // An argon2 session has no v1 personal key (rawKeyResearcher is ""): its keys are re-derived
    // from the key-derivation salt sign-in stores.
    return Boolean(localStorage.getItem('jwt'))
        && Boolean(localStorage.getItem('rawKeyResearcher') || localStorage.getItem('keyDerivationSalt'));
}


function AcceptInvitationPage(props) {
    const fragment = useRef(null);
    const [view, setView] = useState('loading');
    const [invitation, setInvitation] = useState({});
    const [busy, setBusy] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    // A ref, not state: a second click in the same tick must see the first one in flight.
    const inFlight = useRef(false);

    useEffect(() => {
        // Always take the parked link, so a stale one never outlives this visit; a fresh link in
        // the URL (normally captured at boot, see bootAcceptFragment.ts) wins over it.
        const parked = takePendingInvitation();
        const parsed = takeBootAcceptFragment() || consumeAcceptFragment(window) || parked;
        if (!parsed) {
            setView('invalid');
            return;
        }
        fragment.current = parsed;
        fetchInvitationStatus(parsed).then((status) => {
            setInvitation(status);
            if (status.state === 'valid') {
                setView(status.needsAccount ? 'valid_new' : 'valid_existing');
            } else {
                setView(VIEW_FOR_STATE[status.state] || 'error');
            }
        });
    }, []);

    function goSignIn() {
        stashPendingInvitation(fragment.current);
        props.history.push(SIGN_IN_ROUTE, { from: ACCEPT_INVITATION_PATH });
    }

    async function accept() {
        let investigations;
        try {
            investigations = (await fetchInvestigations()).investigations || [];
        } catch (error) {
            // Only an expired session sends the invitee to sign in; anything else is an error,
            // not a reason to throw a working session away.
            if (error && (error.status === 401 || (error.response && error.response.status === 401))) {
                goSignIn();
            } else {
                setView('error');
            }
            return;
        }
        const target = investigations.find((inv) => inv.uuid === invitation.investigationUuid);
        if (!target) {
            // Signed in as someone this invitation is not for.
            goSignIn();
            return;
        }
        const result = await acceptTokenInvitation({
            fragment: fragment.current,
            investigationUuid: target.uuid,
            wrappedKey: target.keyResearcherInvestigation,
            rawKeyResearcher: localStorage.getItem('rawKeyResearcher'),
            invitationKeks: invitation.wrappedKeks || [],
        });
        if (result.ok) {
            fragment.current = null;
            setView('success');
        } else {
            setView(VIEW_FOR_STATE[result.reason] || 'error');
        }
    }

    async function guarded(action) {
        if (inFlight.current) {
            return;
        }
        inFlight.current = true;
        setBusy(true);
        try {
            await action();
        } finally {
            inFlight.current = false;
            setBusy(false);
        }
    }

    function onAccept() {
        if (!hasSession()) {
            goSignIn();
            return;
        }
        return guarded(accept);
    }

    function onRegister(values) {
        return guarded(() => register(values));
    }

    async function register(values) {
        const registered = await registerInvitedResearcher({
            email: invitation.email,
            name: values.name,
            surnames: values.surnames,
            password: values.password,
            language: props.activeLanguage ? props.activeLanguage.code : 'en',
            cloudflareToken: captchaToken,
        }, fragment.current, invitation.wrappedKeks || []);
        if (!registered.ok) {
            setView(registered.invalid || registered.status === 401 ? 'invalid' : 'error');
            return;
        }
        if (isArgon2Auth()) {
            // registerV2 has accepted the membership and stored its KEKs under the new account's
            // key in one transaction: the invitation is complete. Signing in only opens the session
            // (handleLoginKeks unwraps the now-final KEKs); if it fails, the account still works.
            fragment.current = null;
            try {
                await signIn({ email: invitation.email, password: values.password }, 'researcher');
            } catch (error) {
                props.history.push(SIGN_IN_ROUTE);
                return;
            }
            setView('success');
            return;
        }
        try {
            await signIn({ email: invitation.email, password: values.password }, 'researcher');
        } catch (error) {
            // The account exists now; signing in by hand and coming back finishes the accept.
            goSignIn();
            return;
        }
        await accept();
    }

    function onRequestResend() {
        return guarded(async () => {
            const sent = await requestInvitationResend(fragment.current);
            if (sent) {
                fragment.current = null;
                setView('resend_requested');
            } else {
                setView('error');
            }
        });
    }

    function onContinue() {
        if (import.meta.env.VITE_APP_PRODUCT === 'HOSPITAL') {
            window.location.href = '/';
        } else {
            props.history.push('/investigation/show/' + invitation.investigationUuid);
        }
    }

    return (
        <>
            <Helmet>
                <meta name="referrer" content="no-referrer" />
            </Helmet>
            <AcceptInvitation
                view={view}
                investigationName={invitation.investigationName}
                inviterName={invitation.inviterName}
                email={invitation.email}
                busy={busy || (view === 'valid_new' && !captchaToken)}
                captcha={view === 'valid_new' ? <CloudFlare callback={setCaptchaToken} /> : null}
                onRegister={onRegister}
                onAccept={onAccept}
                onRequestResend={onRequestResend}
                onContinue={onContinue}
            />
        </>
    );
}

export default withLocalize(AcceptInvitationPage);
